import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionContext';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

// PermissionGuard component to protect routes based on user permissions
const PermissionGuard = ({
  children,
  interfaceKey,      // Interface key to check access for (e.g., 'items-detail')
  requiredAdmin,     // If true, requires admin access
  requiredOwner,     // If true, requires owner access
  fallbackPath,      // Path to redirect to if access denied (default: shows access denied message)
  showLoading = true // Show loading spinner while checking permissions
}) => {
  const { hasAccess, isAdmin, isOwner, loading } = usePermissions();

  // Show loading state while permissions are being fetched
  if (loading && showLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user has required permissions
  let hasPermission = true;

  if (requiredOwner) {
    hasPermission = isOwner;
  } else if (requiredAdmin) {
    hasPermission = isAdmin || isOwner;
  } else if (interfaceKey) {
    hasPermission = hasAccess(interfaceKey);
  }

  // If no permission, redirect or show access denied
  if (!hasPermission) {
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={4}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
            backgroundColor: '#f5f5f5'
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this feature.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please contact your administrator if you believe this is an error.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // User has permission, render children
  return children;
};

// AdminGuard - shorthand for requiring admin access
export const AdminGuard = ({ children, fallbackPath }) => (
  <PermissionGuard requiredAdmin fallbackPath={fallbackPath}>
    {children}
  </PermissionGuard>
);

// OwnerGuard - shorthand for requiring owner access
export const OwnerGuard = ({ children, fallbackPath }) => (
  <PermissionGuard requiredOwner fallbackPath={fallbackPath}>
    {children}
  </PermissionGuard>
);

export default PermissionGuard;
